'use client';

import { ShoppingCart, Flame, Sparkles, Package, Check, ArrowRight, Star } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import SmartImage from '@/components/ui/SmartImage';
import type { Product } from '@/types';
import { useCartStore } from '@/store/cartStore';

interface ProductCardProps {
  product: Product;
  index?: number;
  isSpotlight?: boolean;
}

function formatPrice(price: number): string {
  return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 }).format(price);
}
function calcDiscount(original: number | undefined, current: number): number {
  if (!original || original <= current) return 0;
  return Math.round((1 - current / original) * 100);
}
// The card feature checklist comes from the product description, one item per
// line (admins write features line-by-line). Strip common bullet markers.
/** Số dòng tính năng MỌI thẻ đều chừa chỗ — xem chú thích ở khối danh sách. */
const SO_DONG_TINH_NANG = 3;

function featureLines(desc: string | undefined): string[] {
  return (desc || '')
    // Mô tả sản phẩm được soạn bằng HTML (`<br>`, `<strong>`), nên tách theo
    // `\n` thôi là cả đoạn dính thành MỘT dòng dài rồi bị `line-clamp-1` cắt
    // — thẻ trông như chỉ có một tính năng duy nhất.
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .split(/\r?\n/)
    .map((l) => l.replace(/^\s*(?:[-•*]|✅|✓)\s*/, '').trim())
    // "Tính năng:" là tiêu đề của đoạn, không phải một tính năng — để lại thì
    // nó chiếm mất một trong ba dòng ít ỏi.
    .filter((l) => l && !/^t[ií]nh n[ăa]ng\s*:?$/i.test(l))
    .slice(0, SO_DONG_TINH_NANG);
}

export default function ProductCard({ product }: ProductCardProps) {
  const router = useRouter();
  const addShopItem = useCartStore((s) => s.addShopItem);

  const discount = calcDiscount(product.originalPrice, product.price);
  const features = featureLines(product.description);
  const isPhysical = product.productType === 'PHYSICAL';
  // ⚠️ Trước 14/09/2026 điều kiện là `isPhysical && stock <= 0`, tức HÀNG SỐ
  // KHÔNG BAO GIỜ báo hết hàng. Với sản phẩm bán key (tồn kho = số key còn
  // trong kho) thì đó là: kho cạn, thẻ vẫn mời mua, nút "Mua ngay" vẫn bấm
  // được, và người dùng chỉ biết khi thanh toán xong mà không nhận được gì.
  // Backend đã chặn ở lúc đặt đơn (206700ea), nhưng để người ta bấm rồi mới
  // chặn là một trải nghiệm tệ, không phải một chốt an toàn.
  const outOfStock = product.stock <= 0;
  const href = `/shop/${product.slug}`;

  const buyNow = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!outOfStock) { addShopItem(product); router.push('/checkout'); }
  };

  const saved = product.originalPrice && product.originalPrice > product.price
    ? product.originalPrice - product.price
    : 0;
  const sold = product.soldCount ?? 0;

  // ⚠️ THẺ KHÔNG TỰ CHẠY HIỆU ỨNG VÀO NỮA (22/09/2026).
  // Trước đây thẻ là `motion.div` với `initial={reduce ? false : {opacity:0}}`
  // và `animate={reduce ? undefined : {opacity:1}}`. `useReduceAnimations()`
  // trả `false` ở lần render đầu rồi mới lật `true` trong effect trên máy CẢM
  // ỨNG ⇒ thẻ mount ở opacity 0, hiệu ứng vừa bắt đầu thì `animate` bị rút
  // thành `undefined` ⇒ framer DỪNG giữa chừng. Trên iPad/điện thoại: thẻ đầu
  // mờ căm ở ~20%, ba thẻ sau (còn đang chờ `delay`) kẹt ở 0 — "4 sản phẩm"
  // mà chỉ thấy một cái bóng. Desktop không dính vì chuột ⇒ `reduce` không đổi.
  // Hiệu ứng vào do lưới ở `app/shop/page.tsx` lo; hover làm bằng CSS và chỉ
  // bật trên thiết bị có chuột (`[@media(hover:hover)]`).
  return (
    <div className="group relative h-full transition-transform duration-300 [@media(hover:hover)]:hover:-translate-y-1.5">
      <Link
        href={href}
        className="relative flex h-full flex-col overflow-hidden rounded-2xl border transition-all"
        style={{
          background: 'linear-gradient(165deg, rgba(23,20,38,0.92), rgba(12,10,22,0.94))',
          borderColor: 'rgba(168,85,247,0.18)',
          boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
        }}
      >
        {/* hover glow ring */}
        <span className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-300 [@media(hover:hover)]:group-hover:opacity-100"
          style={{ boxShadow: '0 0 0 1px rgba(168,85,247,0.45), 0 18px 50px rgba(124,58,237,0.28)' }} />

        {/* ── Media ── */}
        <div className="relative">
          <div className="relative aspect-[4/3] overflow-hidden" style={{ background: 'radial-gradient(120% 120% at 50% 0%, rgba(99,102,241,0.18), rgba(10,8,20,0.9))' }}>
            {product.thumbnail ? (
              <SmartImage src={product.thumbnail} alt={product.name} className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 [@media(hover:hover)]:group-hover:scale-105" />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center"><Package className="h-10 w-10 text-white/25" /></div>
            )}
            {/* top badges */}
            <div className="absolute left-3 top-3 flex flex-col gap-1.5">
              {product.isHot && (
                <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold text-white shadow" style={{ background: 'linear-gradient(135deg,#f97316,#ef4444)' }}>
                  <Flame className="h-3 w-3" /> Hot
                </span>
              )}
              {product.isNew && (
                <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold text-white shadow" style={{ background: 'linear-gradient(135deg,#22d3ee,#3b82f6)' }}>
                  <Sparkles className="h-3 w-3" /> New
                </span>
              )}
            </div>
            {/* discount + category */}
            <div className="absolute right-3 top-3 flex flex-col items-end gap-1.5">
              {discount > 0 && (
                <span className="rounded-full px-2.5 py-1 text-[11px] font-bold text-white shadow" style={{ background: 'linear-gradient(135deg,#16a34a,#22c55e)' }}>
                  -{discount}%
                </span>
              )}
              {product.category && (
                <span className="rounded-md px-2 py-0.5 text-[10px] font-medium text-neon-violet backdrop-blur-sm" style={{ background: 'rgba(168,85,247,0.14)' }}>
                  {product.category}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="flex flex-1 flex-col p-4">
          <h3 className="line-clamp-2 min-h-[2.6rem] text-[15px] font-heading font-bold leading-snug text-text-primary transition-colors group-hover:text-neon-violet">
            {product.name}
          </h3>

          {/* Rating */}
          {(product.reviewCount ?? 0) > 0 && (
            <div className="mt-1 flex items-center gap-1 text-xs text-text-muted">
              <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
              <span className="font-medium text-text-secondary">{(product.rating ?? 0).toFixed(1)}</span>
              <span>({product.reviewCount})</span>
            </div>
          )}

          {/* ── Danh sách tính năng ──
              CHỪA CHỖ CỐ ĐỊNH cho đúng 3 dòng, kể cả khi sản phẩm chỉ có 1.
              Không có `min-h` thì thẻ nào mô tả dài hơn sẽ cao hơn, và vì lưới
              kéo mọi thẻ trong một hàng bằng nhau, nút "Xem sản phẩm" của các
              thẻ nằm ở những độ cao khác nhau — đúng chỗ người dùng chỉ ra
              14/09/2026 ("sao các sản phẩm không đồng đều vậy").
              `mt-auto` ở khối giá bên dưới mới là thứ ghim đáy; `min-h` ở đây
              để phần chữ cũng thẳng hàng chứ không chỉ mỗi cái nút. */}
          <ul className="mt-3 space-y-1.5" style={{ minHeight: `${SO_DONG_TINH_NANG * 1.375}rem` }}>
            {features.map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-text-secondary">
                <Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-emerald-400" />
                <span className="line-clamp-1">{f}</span>
              </li>
            ))}
          </ul>

          {/* Price — `mt-auto` đẩy cả khối giá + nút xuống ĐÁY thẻ, nên mọi
              thẻ trong cùng một hàng có nút thẳng hàng nhau bất kể mô tả dài
              ngắn thế nào. */}
          <div className="mt-auto pt-4">
            <div className="flex items-end gap-2">
              <span className="text-xl font-heading font-bold text-text-primary tabular-nums">{formatPrice(product.price)}</span>
              {saved > 0 && (
                <span className="mb-0.5 text-xs text-text-muted line-through tabular-nums">{formatPrice(product.originalPrice!)}</span>
              )}
            </div>
            <div className="mt-1 flex min-h-[1rem] items-center gap-2 text-[11px] text-text-muted">
              {saved > 0 && <span className="font-medium text-emerald-400">Tiết kiệm {formatPrice(saved)}</span>}
              {saved > 0 && sold > 0 && <span aria-hidden>·</span>}
              {sold > 0 && <span>Đã bán {sold}</span>}
            </div>
          </div>

          {/* Actions — nút chính là "Xem chi tiết" (cả thẻ là một liên kết),
              nút giỏ bên phải là lối tắt MUA NGAY cho người đã biết mình cần gì. */}
          <div className="mt-3 flex gap-2">
            <span
              className="flex flex-1 items-center justify-center gap-1.5 rounded-xl py-2.5 text-sm font-semibold text-white transition-opacity group-hover:opacity-90"
              style={{ background: 'linear-gradient(135deg,#6366f1,#a855f7)', boxShadow: '0 6px 18px rgba(124,58,237,0.3)' }}
            >
              Xem chi tiết
              <ArrowRight className="h-4 w-4 transition-transform [@media(hover:hover)]:group-hover:translate-x-0.5" />
            </span>
            <button
              type="button"
              onClick={buyNow}
              disabled={outOfStock}
              aria-label={outOfStock ? 'Hết hàng' : `Mua ngay ${product.name}`}
              title={outOfStock ? 'Hết hàng' : 'Mua ngay'}
              className="flex min-w-[44px] items-center justify-center rounded-xl border px-3 transition-colors hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-40"
              style={{ borderColor: 'rgba(168,85,247,0.3)', color: '#c084fc' }}
            >
              <ShoppingCart className="h-4 w-4" />
            </button>
          </div>
          {outOfStock && <p className="mt-2 text-center text-[11px] font-medium text-red-400">Tạm hết hàng</p>}
        </div>
      </Link>
    </div>
  );
}
